import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, MapPin, Phone, Globe, Mail, Star, Download, Trash2,
  RefreshCw, ChevronLeft, ChevronRight, CheckSquare, Square,
  AlertCircle, ExternalLink, Building2, Clock,
  Filter, X, Plus, Loader2, CheckCircle2, XCircle, BarChart2,
  ArrowLeft, Copy, Save, RotateCcw, ShieldCheck, SendHorizontal, Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import ModuleDemoVideo from '../components/ModuleDemoVideo';

const API = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '')
  : '';

const DEFAULT_LEAD_CREDIT_PACKS = [
  { id: 'pack_100', label: '100 Lead Credits', credits: 100, usd: 9.0, description: 'Scrape and enrich ~100 leads.' },
  { id: 'pack_500', label: '500 Lead Credits', credits: 500, usd: 39.0, description: 'Best value for active outbound campaigns.' },
];

/** Maps is capped by gmaps ScrapeJobCreate (le=200); LinkedIn stays at a practical batch size. */
const MAPS_MAX_RESULTS_OPTIONS = [20, 50, 100, 200];
const LINKEDIN_MAX_RESULTS_OPTIONS = [10, 20, 50, 100];
const RESULTS_PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_RESULTS_PAGE_SIZE = 50;

function affordableMaxResultsOptions(source, creditBalance) {
  const pool = source === 'linkedin_icp' ? LINKEDIN_MAX_RESULTS_OPTIONS : MAPS_MAX_RESULTS_OPTIONS;
  const balance = Math.max(0, Number(creditBalance) || 0);
  if (balance <= 0) return [pool[0]];
  const affordable = pool.filter((n) => n <= balance);
  return affordable.length ? affordable : [pool[0]];
}

function defaultMaxResults(source, creditBalance) {
  const options = affordableMaxResultsOptions(source, creditBalance);
  const balance = Math.max(0, Number(creditBalance) || 0);
  const preferred = Math.min(50, balance || 20);
  const atLeast20 = options.find((n) => n >= Math.min(20, preferred));
  if (options.includes(preferred)) return preferred;
  if (atLeast20 != null) return atLeast20;
  return options[0];
}

const apiPath = (path) => {
  const normalized = path.startsWith('/api') ? path : `/api/${path.replace(/^\//, '')}`;
  return API ? `${API}${normalized}` : normalized;
};

const appendWebsiteScope = (url, websiteId = '') => {
  if (!websiteId) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}website_id=${encodeURIComponent(websiteId)}`;
};

/** Job operations must use the job's own workspace, not the list filter dropdown. */
const jobWebsiteScope = (job) => (job?.website_id || '').trim();

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const buildScrapePayloadFromJob = (job, websiteIdFallback = '') => ({
  query: job.query,
  location: job.location || '',
  max_results: Number(job.max_results || 20),
  min_rating: Number(job.min_rating || 0),
  language: job.language || 'en',
  require_phone: !!job.require_phone,
  require_website: !!job.require_website,
  require_email: !!job.require_email,
  extract_emails: !!job.extract_emails,
  website_id: job.website_id || websiteIdFallback || '',
});

async function parseJsonResponse(res) {
  const rawText = await res.clone().text();
  try {
    return rawText ? JSON.parse(rawText) : {};
  } catch {
    return {};
  }
}

const statusColor = {
  pending: 'text-yellow-400 bg-yellow-400/10',
  running: 'text-blue-400 bg-blue-400/10',
  done: 'text-green-400 bg-green-400/10',
  failed: 'text-red-400 bg-red-400/10',
};

const statusIcon = {
  pending: <Clock className="w-3.5 h-3.5" />,
  running: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
  done: <CheckCircle2 className="w-3.5 h-3.5" />,
  failed: <XCircle className="w-3.5 h-3.5" />,
};

function ProgressBar({ value }) {
  return (
    <div className="w-full bg-white/10 rounded-full h-1.5 mt-1">
      <div
        className="bg-violet-500 h-1.5 rounded-full transition-all duration-500"
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

function LeadCreditsPanel({ balance, costs, packs, enabled, stripeConfigured, onRefresh, onBalanceChange }) {
  const [open, setOpen] = useState(false);
  const [busyPack, setBusyPack] = useState('');
  const [mockSession, setMockSession] = useState(null);
  const displayPacks = (packs && packs.length) ? packs : DEFAULT_LEAD_CREDIT_PACKS;

  const buyPack = async (packId) => {
    if (stripeConfigured === false) {
      toast.error('Configure Stripe in Settings → API Keys (platform billing) to buy lead credits.');
      return;
    }
    setBusyPack(packId);
    try {
      const origin = window.location.origin;
      const res = await fetch(apiPath('/lead-credits/checkout'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          pack_id: packId,
          region: 'us',
          provider: 'stripe',
          success_url: `${origin}/lead-scraper?credits_payment=stripe&session_id={SESSION_ID}`,
          cancel_url: `${origin}/lead-scraper?credits_payment=cancelled`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : (data.detail?.message || 'Checkout failed'));
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      if (data.mode === 'mock') {
        setMockSession(data);
        setOpen(true);
        return;
      }
      toast.error('Could not start checkout');
    } catch (err) {
      toast.error(err.message || 'Checkout failed');
    } finally {
      setBusyPack('');
    }
  };

  const confirmMock = async () => {
    if (!mockSession?.session_id) return;
    setBusyPack('mock');
    try {
      const res = await fetch(apiPath(`/lead-credits/checkout/${encodeURIComponent(mockSession.session_id)}/confirm-mock`), {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Payment failed');
      toast.success(data.message || `Added ${data.credits_added || mockSession.credits} credits`);
      setMockSession(null);
      setOpen(false);
      onBalanceChange?.(data.balance);
      onRefresh?.();
    } catch (err) {
      toast.error(err.message || 'Payment failed');
    } finally {
      setBusyPack('');
    }
  };

  if (!enabled) return null;

  const modal = open ? createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="lead-credits-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-gray-900 p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Lead credits</h3>
          <button type="button" onClick={() => { setOpen(false); setMockSession(null); }} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-white/60 mb-4">
          Balance: <span className="text-white font-semibold">{balance}</span>
          {' · '}Scrape {costs?.scrape_lead || 1} credit/lead
          {' · '}Email +{costs?.email_enrichment || 1}
          {' · '}Contact +{costs?.contact_enrichment || 1}
        </p>

        {stripeConfigured === false && (
          <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100" data-testid="lead-credits-stripe-missing">
            Stripe is not configured for purchases. Ask a platform admin to add the Stripe secret key in Settings → API Keys.
          </div>
        )}

        {mockSession ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
            <p className="text-sm text-amber-100">Mock payment — confirm to add {mockSession.credits} credits (${mockSession.amount}).</p>
            <button
              type="button"
              onClick={confirmMock}
              disabled={!!busyPack}
              className="w-full rounded-xl bg-amber-500 py-2.5 text-sm font-semibold text-black hover:bg-amber-400 disabled:opacity-50"
            >
              {busyPack ? 'Processing…' : 'Confirm mock payment'}
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="lead-credits-pack-list">
            {displayPacks.map((pack) => (
              <button
                key={pack.id}
                type="button"
                onClick={() => buyPack(pack.id)}
                disabled={!!busyPack || stripeConfigured === false}
                className="w-full text-left rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/10 disabled:opacity-50"
                data-testid={`lead-credits-pack-${pack.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-white">{pack.label}</span>
                  <span className="text-sm text-violet-300">${pack.usd}</span>
                </div>
                <p className="text-xs text-white/50 mt-1">{pack.description}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div className="flex items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-1.5" data-testid="lead-credits-balance">
        <Coins className="w-4 h-4 text-violet-300" />
        <span className="text-xs text-violet-100">
          <span className="font-semibold text-white">{balance}</span> credits
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] font-medium rounded-md bg-violet-500/30 px-2 py-0.5 text-violet-100 hover:bg-violet-500/40"
          data-testid="lead-credits-buy-button"
        >
          Buy credits
        </button>
      </div>
      {modal}
    </>
  );
}

function PipelineOverview({
  jobId,
  jobWebsiteId = '',
  jobStatus = '',
  jobProgress = 0,
  jobResultCount = 0,
  onOpenReview,
  onBulkRetry,
}) {
  const [pipeline, setPipeline] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const warnedAiRef = useRef('');

  const loadPipeline = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const scopeWebsiteId = jobWebsiteId || '';
      const [jobRes, healthRes] = await Promise.all([
        fetch(appendWebsiteScope(apiPath(`/leads/jobs/${jobId}`), scopeWebsiteId), { headers: authHeaders() }),
        fetch(appendWebsiteScope(apiPath(`/leads/jobs/${jobId}/health`), scopeWebsiteId), { headers: authHeaders() }),
      ]);
      const [jobText, healthText] = await Promise.all([jobRes.text(), healthRes.text()]);
      let data = {};
      let healthData = {};
      try { data = jobText ? JSON.parse(jobText) : {}; } catch { data = {}; }
      try { healthData = healthText ? JSON.parse(healthText) : {}; } catch { healthData = {}; }
      if (!jobRes.ok) {
        setPipeline(null);
        setHealth(null);
        setLoadError(data.detail || 'Pipeline stats are not available for this job yet.');
        return;
      }
      setPipeline(data);
      setHealth(healthRes.ok ? healthData : null);
      setLoadError('');
      const aiWarn = data.ai_summary_warning || data.warning || '';
      if (aiWarn && warnedAiRef.current !== `${jobId}:${aiWarn}`) {
        warnedAiRef.current = `${jobId}:${aiWarn}`;
        toast.error(aiWarn, { duration: 9000 });
      }
    } catch {
      setPipeline(null);
      setHealth(null);
      setLoadError('Could not load pipeline stats. Try refreshing.');
    } finally {
      setLoading(false);
    }
  }, [jobId, jobWebsiteId]);

  // Reload when the parent poll updates scrape progress or the job finishes.
  useEffect(() => { loadPipeline(); }, [loadPipeline, jobStatus, jobProgress, jobResultCount]);

  // Keep pipeline counters moving while the scrape/enrichment job is still running.
  useEffect(() => {
    if (!jobId || (jobStatus !== 'pending' && jobStatus !== 'running')) return undefined;
    const id = setInterval(loadPipeline, 5000);
    return () => clearInterval(id);
  }, [jobId, jobStatus, loadPipeline]);

  if (!jobId) return null;

  const pipelinePending = jobStatus === 'pending' || jobStatus === 'running';

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4" data-testid="lead-pipeline-overview-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">Pipeline Overview</h4>
          <p className="text-xs text-white/45">New jobs now flow through raw records → canonical entities → enriched CRM leads.</p>
        </div>
        <button
          type="button"
          onClick={loadPipeline}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/60 hover:text-white transition-colors"
          data-testid="lead-pipeline-refresh-button"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {!pipeline ? (
        <div className="text-xs text-white/40" data-testid="lead-pipeline-empty-state">
          {pipelinePending
            ? 'Pipeline stats update as enrichment runs. Counters refresh automatically while this job is in progress.'
            : (loadError || 'Pipeline details will appear when this job finishes the new enrichment flow.')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              ['Raw records', pipeline.source_record_count || 0, 'lead-pipeline-source-count'],
              ['Canonical entities', pipeline.entity_count || 0, 'lead-pipeline-entity-count'],
              ['CRM auto-saved', pipeline.crm_auto_saved_count || 0, 'lead-pipeline-crm-count'],
              ['Emails enriched', pipeline.email_enriched_count || 0, 'lead-pipeline-email-count'],
              ['Contacts enriched', pipeline.contact_enriched_count || 0, 'lead-pipeline-contact-count'],
              ['Legacy linked', pipeline.legacy_linked_count || 0, 'lead-pipeline-legacy-count'],
            ].map(([label, value, testId]) => (
              <div key={testId} className="rounded-xl border border-white/10 bg-black/20 px-3 py-3" data-testid={testId}>
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">{label}</p>
                <p className="mt-1 text-2xl font-semibold text-white">{value}</p>
              </div>
            ))}
          </div>

          {health && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="lead-pipeline-health-grid">
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3" data-testid="lead-pipeline-partial-health-card">
                <p className="text-[11px] uppercase tracking-[0.16em] text-amber-100/60">Partial leads</p>
                <p className="mt-1 text-2xl font-semibold text-amber-100">{health.partial_entities || 0}</p>
                <p className="text-xs text-amber-100/70 mt-1">Safely queued for retry when enrichment partially completes.</p>
              </div>
              <div className="rounded-xl border border-sky-500/20 bg-sky-500/10 px-4 py-3" data-testid="lead-pipeline-provider-health-card">
                <p className="text-[11px] uppercase tracking-[0.16em] text-sky-100/60">Enrichment health</p>
                <p className="mt-1 text-sm text-sky-100">
                  Email ok: {(health.providers?.email_enrichment?.ok ?? health.providers?.hunter?.ok) || 0}
                  {' · '}fail: {(health.providers?.email_enrichment?.failed ?? health.providers?.hunter?.failed) || 0}
                </p>
                <p className="text-sm text-sky-100">
                  Contacts ok: {(health.providers?.contact_enrichment?.ok ?? health.providers?.apollo?.ok) || 0}
                  {' · '}fail: {(health.providers?.contact_enrichment?.failed ?? health.providers?.apollo?.failed) || 0}
                </p>
              </div>
              <div className="rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/10 px-4 py-3" data-testid="lead-pipeline-retry-health-card">
                <p className="text-[11px] uppercase tracking-[0.16em] text-fuchsia-100/60">Retry queue</p>
                <p className="mt-1 text-sm text-fuchsia-100">Queued: {health.retry_queued || 0} · Running: {health.retry_running || 0}</p>
                <p className="text-sm text-fuchsia-100">Failed: {health.retry_failed || 0}</p>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/8 px-4 py-3" data-testid="lead-pipeline-crm-banner">
            <p className="text-sm text-emerald-300 font-medium">CRM auto-save is active for this new pipeline.</p>
            <p className="text-xs text-emerald-200/70 mt-1">Marketing audience sync stays manual, so you control when enriched leads enter Email, SMS, or WhatsApp. Sync uses this job&apos;s workspace scope.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onOpenReview(['email'])}
              className="text-xs rounded-xl px-3 py-2 bg-blue-500/15 text-blue-200 hover:bg-blue-500/25 transition-colors"
              data-testid="lead-pipeline-sync-email-button"
            >
              Sync Email Audience
            </button>
            <button
              type="button"
              onClick={() => onOpenReview(['sms'])}
              className="text-xs rounded-xl px-3 py-2 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 transition-colors"
              data-testid="lead-pipeline-sync-sms-button"
            >
              Sync SMS Contacts
            </button>
            <button
              type="button"
              onClick={() => onOpenReview(['whatsapp'])}
              className="text-xs rounded-xl px-3 py-2 bg-green-500/15 text-green-200 hover:bg-green-500/25 transition-colors"
              data-testid="lead-pipeline-sync-whatsapp-button"
            >
              Sync WhatsApp Contacts
            </button>
            <button
              type="button"
              onClick={() => onOpenReview(['email', 'sms'])}
              className="text-xs rounded-xl px-3 py-2 bg-white/10 text-white hover:bg-white/20 transition-colors"
              data-testid="lead-pipeline-open-review-button"
            >
              <SendHorizontal className="w-3.5 h-3.5 inline mr-1" /> Review Before Sync
            </button>
            <button
              type="button"
              onClick={onBulkRetry}
              className="text-xs rounded-xl px-3 py-2 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 transition-colors"
              data-testid="lead-pipeline-retry-partial-button"
            >
              <RotateCcw className="w-3.5 h-3.5 inline mr-1" /> Retry Partial Leads
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MarketingReviewModal({ open, onClose, jobId, selectedWebsiteId, defaultChannels, onConfirmSync }) {
  const [entities, setEntities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState({ email: true, sms: true, whatsapp: false });

  useEffect(() => {
    if (!open) return;
    setChannels({
      email: defaultChannels.includes('email'),
      sms: defaultChannels.includes('sms'),
      whatsapp: defaultChannels.includes('whatsapp'),
    });
  }, [open, defaultChannels]);

  const loadReview = useCallback(async () => {
    if (!open || !jobId) return;
    setLoading(true);
    try {
      const res = await fetch(apiPath(`/leads/jobs/${jobId}/review-marketing`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ website_id: selectedWebsiteId || '' }),
      });
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!res.ok) {
        toast.error(data.detail || 'Failed to load review list');
        return;
      }
      setEntities(data.entities || []);
      setSummary(data.summary || null);
      setSelected(new Set((data.entities || []).map((entity) => entity.id)));
    } catch {
      toast.error('Failed to load review list');
    } finally {
      setLoading(false);
    }
  }, [open, jobId, selectedWebsiteId]);

  useEffect(() => { loadReview(); }, [loadReview]);

  if (!open) return null;

  const selectedChannels = Object.entries(channels).filter(([, enabled]) => enabled).map(([key]) => key);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="lead-pipeline-review-modal">
      <div className="w-full max-w-5xl rounded-3xl border border-white/10 bg-[#09090f] p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-white">Review leads before audience sync</h3>
            <p className="text-sm text-white/50 mt-1">Choose channels and confirm which enriched leads should enter marketing.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="lead-pipeline-review-close-button"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="lead-pipeline-review-summary-grid">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4"><p className="text-xs text-white/40 uppercase">Selected</p><p className="text-2xl text-white font-semibold mt-1">{selected.size}</p></div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4"><p className="text-xs text-white/40 uppercase">Email ready</p><p className="text-2xl text-white font-semibold mt-1">{summary?.email_ready || 0}</p></div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4"><p className="text-xs text-white/40 uppercase">SMS ready</p><p className="text-2xl text-white font-semibold mt-1">{summary?.sms_ready || 0}</p></div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4"><p className="text-xs text-white/40 uppercase">WhatsApp ready</p><p className="text-2xl text-white font-semibold mt-1">{summary?.whatsapp_ready || 0}</p></div>
        </div>

        <div className="flex flex-wrap gap-3" data-testid="lead-pipeline-review-channel-pills">
          {[
            ['email', 'Email audience'],
            ['sms', 'SMS contacts'],
            ['whatsapp', 'WhatsApp contacts'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setChannels(prev => ({ ...prev, [key]: !prev[key] }))}
              className={`rounded-full px-4 py-2 text-xs border transition-colors ${channels[key] ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-200' : 'border-white/10 bg-white/5 text-white/45'}`}
              data-testid={`lead-pipeline-review-channel-${key}`}
            >
              {channels[key] ? <ShieldCheck className="w-3.5 h-3.5 inline mr-1" /> : null}
              {label}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="lead-pipeline-review-entity-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="px-3 py-3 text-left text-white/60"><button onClick={() => setSelected(new Set(selected.size === entities.length ? [] : entities.map((entity) => entity.id)))} data-testid="lead-pipeline-review-toggle-all-button">{selected.size === entities.length && entities.length ? <CheckSquare className="w-4 h-4 text-violet-400" /> : <Square className="w-4 h-4 text-white/35" />}</button></th>
                <th className="px-3 py-3 text-left text-white/60">Lead</th>
                <th className="px-3 py-3 text-left text-white/60">Contactability</th>
                <th className="px-3 py-3 text-left text-white/60">Pipeline</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-10 text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading review list…</td></tr>
              ) : entities.map((entity) => (
                <tr key={entity.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-3 py-3"><button onClick={() => setSelected(prev => { const next = new Set(prev); next.has(entity.id) ? next.delete(entity.id) : next.add(entity.id); return next; })} data-testid="lead-pipeline-review-select-row-button">{selected.has(entity.id) ? <CheckSquare className="w-4 h-4 text-violet-400" /> : <Square className="w-4 h-4 text-white/20" />}</button></td>
                  <td className="px-3 py-3"><p className="text-white font-medium">{entity.business_name}</p><p className="text-xs text-white/40 mt-1">{entity.contact_name || 'No contact name yet'} {entity.contact_title ? `· ${entity.contact_title}` : ''}</p></td>
                  <td className="px-3 py-3 text-xs text-white/60"><p>Email: {entity.email || '—'}</p><p>Phone: {entity.phone || '—'}</p></td>
                  <td className="px-3 py-3 text-xs text-white/60"><p>Enrichment: {entity.enrichment_status || 'pending'}</p><p>CRM: {entity.crm_sync_status || 'pending'}</p></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-white/45">Only selected leads will be pushed into the chosen marketing audiences.</p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/10 text-white/70 hover:bg-white/15" data-testid="lead-pipeline-review-cancel-button">Cancel</button>
            <button
              onClick={() => onConfirmSync(selectedChannels, [...selected])}
              disabled={selected.size === 0 || selectedChannels.length === 0}
              className="px-4 py-2 rounded-xl bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40"
              data-testid="lead-pipeline-review-confirm-button"
            >
              Confirm Sync
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewJobForm({ onCreated, selectedWebsiteId, onInsufficientCredits, creditBalance = 0 }) {
  const [source, setSource] = useState('google_maps');
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({
    query: '', location: '', max_results: defaultMaxResults('google_maps', creditBalance), min_rating: 0,
    require_phone: false, require_website: false, extract_emails: false, language: 'en',
    icp_text: '',
  });
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState({ delivered: 0, remaining_estimate: 60, exhausted_likely: false });
  const maxResultsTouchedRef = useRef(false);
  const set = (k, v) => {
    if (k === 'max_results') maxResultsTouchedRef.current = true;
    setForm(prev => ({ ...prev, [k]: v }));
  };
  const isMaps = source === 'google_maps';
  const isLinkedIn = source === 'linkedin_icp';
  const selectedSourceMeta = sources.find((s) => s.id === source);
  const maxResultsOptions = affordableMaxResultsOptions(source, creditBalance);
  const batchSize = Number(form.max_results) || maxResultsOptions[0] || 20;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiPath('/lead-jobs/sources'), { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSources(data.sources || []);
      } catch {
        // Sources catalog is best-effort; form still works with defaults.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep max_results within the affordable option set when source or credits change.
  // Until the user picks a value, prefer min(50, credits).
  useEffect(() => {
    const options = affordableMaxResultsOptions(source, creditBalance);
    const preferred = defaultMaxResults(source, creditBalance);
    setForm((prev) => {
      const current = Number(prev.max_results);
      if (!options.includes(current) || !maxResultsTouchedRef.current) {
        if (current === preferred) return prev;
        return { ...prev, max_results: preferred };
      }
      return prev;
    });
  }, [source, creditBalance]);

  // Refresh the "already-delivered" counter whenever the user finishes typing
  // a query or location — lets them see "you've already grabbed 20 leads for
  // this exact query — running again will fetch the NEXT 20".
  useEffect(() => {
    if (!isMaps || !form.query.trim()) { setCursor({ delivered: 0, remaining_estimate: 60, exhausted_likely: false }); return; }
    const id = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ query: form.query.trim(), location: form.location.trim() });
        if (selectedWebsiteId) qs.set('website_id', selectedWebsiteId);
        const res = await fetch(apiPath(`/gmaps/cursor?${qs.toString()}`), { headers: authHeaders() });
        if (res.ok) setCursor(await res.json());
      } catch {
        // ignore cursor prefetch errors
      }
    }, 400);
    return () => clearTimeout(id);
  }, [form.query, form.location, selectedWebsiteId, isMaps]);

  const handleResetCursor = async () => {
    if (!form.query.trim()) { toast.error('Enter a query first.'); return; }
    try {
      const res = await fetch(apiPath('/gmaps/cursor/reset'), {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ query: form.query.trim(), location: form.location.trim(), website_id: selectedWebsiteId || '' }),
      });
      if (!res.ok) { toast.error('Reset failed'); return; }
      setCursor({ delivered: 0, remaining_estimate: 60, exhausted_likely: false });
      toast.success('Cursor reset — next run will start from lead #1.');
    } catch { toast.error('Reset failed'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.query.trim()) { toast.error('Please enter a search query'); return; }
    if (selectedSourceMeta && selectedSourceMeta.ready === false) {
      const key = selectedSourceMeta.needs_platform_key || 'API key';
      toast.error(
        `${selectedSourceMeta.label || 'This source'} is not configured. A platform admin must set ${key} in Settings → API Keys.`,
        { duration: 9000 },
      );
      return;
    }
    setLoading(true);
    try {
      const body = {
        source,
        query: form.query.trim(),
        location: form.location.trim(),
        max_results: Number(form.max_results),
        website_id: selectedWebsiteId || '',
      };
      if (isMaps) {
        Object.assign(body, {
          min_rating: Number(form.min_rating),
          require_phone: !!form.require_phone,
          require_website: !!form.require_website,
          extract_emails: !!form.extract_emails,
          language: form.language || 'en',
        });
      }
      if (isLinkedIn) {
        body.icp_text = (form.icp_text || form.query).trim();
      }
      const res = await fetch(apiPath('/lead-jobs'), {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        if (res.status === 402) {
          const detail = data.detail || {};
          const msg = typeof detail === 'string' ? detail : detail.message || 'Insufficient lead credits';
          toast.error(`${msg} — buy more credits to continue.`, { duration: 8000 });
          onInsufficientCredits?.();
          return;
        }
        if (res.status === 503) {
          const detail = typeof data.detail === 'string' ? data.detail : 'Lead search is not configured. Ask a platform admin to add the required API keys.';
          toast.error(detail, { duration: 9000 });
        } else {
          toast.error(typeof data.detail === 'string' ? data.detail : `Error ${res.status}: Failed to create job`);
        }
        return;
      }
      toast.success(
        isLinkedIn
          ? 'LinkedIn ICP job started!'
          : (cursor.delivered > 0 ? `Fetching next ${form.max_results} unique leads…` : 'Scrape job started!'),
      );
      onCreated(data);
      if (isMaps) {
        try {
          const qs = new URLSearchParams({ query: form.query.trim(), location: form.location.trim() });
          if (selectedWebsiteId) qs.set('website_id', selectedWebsiteId);
          const cursorRes = await fetch(apiPath(`/gmaps/cursor?${qs.toString()}`), { headers: authHeaders() });
          if (cursorRes.ok) setCursor(await cursorRes.json());
        } catch {
          // ignore
        }
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        toast.error('Cannot reach the backend server. Please check your connection or try again.');
      } else {
        toast.error(err.message || 'Failed to create job');
      }
    } finally {
      setLoading(false);
    }
  };

  const sourceOptions = sources.length
    ? sources
    : [
        { id: 'google_maps', label: 'Local businesses', ready: true },
        { id: 'linkedin_icp', label: 'LinkedIn (ICP search)', ready: true },
      ];

  return (
    <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-5">
      <div className="flex items-center gap-2 mb-1">
        <Search className="w-5 h-5 text-violet-400" />
        <h3 className="text-lg font-semibold text-white">New Scrape Job</h3>
      </div>
      <div>
        <label className="block text-xs text-white/60 mb-1.5">Source</label>
        <div className="flex flex-wrap gap-2" data-testid="lead-scraper-source-picker">
          {sourceOptions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                maxResultsTouchedRef.current = false;
                setSource(s.id);
              }}
              data-testid={`lead-scraper-source-${s.id}`}
              className={`rounded-xl px-3 py-2 text-sm border transition-colors ${
                source === s.id
                  ? 'border-violet-500 bg-violet-500/20 text-white'
                  : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              {s.label}
              {s.ready === false && <span className="ml-1 text-[10px] text-amber-300">key needed</span>}
            </button>
          ))}
        </div>
        {isLinkedIn && (
          <p className="mt-2 text-xs text-white/45">
            Discovers LinkedIn profiles via web search. Optional enrichment and ICP qualify need platform keys in Settings.
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-white/60 mb-1.5">Search Query *</label>
          <input
            data-testid="lead-scraper-query-input"
            className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-violet-500 text-sm"
            placeholder={isLinkedIn ? 'e.g. VP Sales B2B SaaS' : 'e.g. restaurants, dentists, gyms...'}
            value={form.query}
            onChange={e => set('query', e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1.5">{isLinkedIn ? 'Region (optional)' : 'Location'}</label>
          <input data-testid="lead-scraper-location-input" className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-violet-500 text-sm" placeholder={isLinkedIn ? 'e.g. United States' : 'e.g. Mumbai, India'} value={form.location} onChange={e => set('location', e.target.value)} />
        </div>
      </div>
      {isLinkedIn && (
        <div>
          <label className="block text-xs text-white/60 mb-1.5">ICP description (optional)</label>
          <textarea
            data-testid="lead-scraper-icp-input"
            className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-violet-500 text-sm min-h-[72px]"
            placeholder="Ideal customer profile — used when you run Qualify on results"
            value={form.icp_text}
            onChange={e => set('icp_text', e.target.value)}
          />
        </div>
      )}
      <div className={`grid gap-4 ${isMaps ? 'grid-cols-3' : 'grid-cols-1 md:grid-cols-3'}`}>
        <div>
          <label className="block text-xs text-white/60 mb-1.5">Max Results</label>
          <select data-testid="lead-scraper-max-results-select" className="w-full bg-white/10 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500" value={form.max_results} onChange={e => set('max_results', Number(e.target.value))}>
            {maxResultsOptions.map(n => <option key={n} value={n} className="bg-gray-900">{n}</option>)}
          </select>
          <p className="mt-1.5 text-[11px] text-white/40" data-testid="lead-scraper-credits-hint">
            1 credit per lead · you have {Number(creditBalance) || 0} credits
          </p>
        </div>
        {isMaps && (
          <>
            <div>
              <label className="block text-xs text-white/60 mb-1.5">Min Rating</label>
              <select data-testid="lead-scraper-min-rating-select" className="w-full bg-white/10 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500" value={form.min_rating} onChange={e => set('min_rating', e.target.value)}>
                {[[0,'Any'],[3,'3+'],[3.5,'3.5+'],[4,'4+'],[4.5,'4.5+']].map(([v,l]) => <option key={v} value={v} className="bg-gray-900">{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1.5">Language</label>
              <select data-testid="lead-scraper-language-select" className="w-full bg-white/10 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500" value={form.language} onChange={e => set('language', e.target.value)}>
                {[['en','English'],['hi','Hindi'],['es','Spanish'],['fr','French'],['de','German'],['ar','Arabic'],['pt','Portuguese'],['ja','Japanese'],['zh','Chinese']].map(([v,l]) => <option key={v} value={v} className="bg-gray-900">{l}</option>)}
              </select>
            </div>
          </>
        )}
      </div>
      {isMaps && (
        <div className="flex flex-wrap gap-4">
          {[['require_phone','Has Phone Number'],['require_website','Has Website'],['extract_emails','Extract Emails (crawls website)']].map(([key,label]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
              <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${form[key] ? 'bg-violet-500 border-violet-500' : 'border-white/30 bg-white/5'}`} onClick={() => set(key, !form[key])}>
                {form[key] && <span className="text-white text-xs">✓</span>}
              </div>
              <span className="text-sm text-white/70">{label}</span>
            </label>
          ))}
        </div>
      )}
      {/* Cross-run cursor banner */}
      {isMaps && form.query.trim() && (
        <div
          className={`flex items-center justify-between gap-3 text-xs rounded-xl px-3 py-2 border ${
            cursor.exhausted_likely
              ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
              : cursor.delivered > 0
                ? 'bg-violet-500/10 border-violet-500/30 text-violet-200'
                : 'bg-white/5 border-white/10 text-white/60'
          }`}
          data-testid="cursor-banner"
        >
          <span>
            {cursor.delivered === 0 && `You haven't scraped this query yet — first run will fetch leads 1–${batchSize}.`}
            {cursor.delivered > 0 && !cursor.exhausted_likely && (
              <>You&apos;ve already collected <b>{cursor.delivered}</b> lead{cursor.delivered === 1 ? '' : 's'} for this query. Next run fetches leads {cursor.delivered + 1}–{cursor.delivered + batchSize}.</>
            )}
            {cursor.exhausted_likely && (
              <>Looks like we&apos;ve scraped most of this area (~150+ leads). Reset to start over or change the query/location.</>
            )}
          </span>
          {cursor.delivered > 0 && (
            <button
              type="button"
              onClick={handleResetCursor}
              data-testid="cursor-reset-btn"
              className="text-[11px] underline hover:no-underline opacity-80 hover:opacity-100"
            >
              Reset
            </button>
          )}
        </div>
      )}
      <button data-testid="lead-scraper-start-job-button" type="submit" disabled={loading} className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        {loading
          ? 'Starting Job...'
          : (isLinkedIn
            ? 'Start LinkedIn ICP search'
            : (cursor.delivered > 0 ? `Fetch Next ${form.max_results} Leads` : 'Start Scraping'))}
      </button>
    </form>
  );
}

function FetchNextBatchPanel({ job, loading, onFetchNext }) {
  const jobWebsiteId = job?.website_id || '';
  const batchSize = Number(job?.max_results || 20);
  const [cursor, setCursor] = useState({ delivered: 0, remaining_estimate: 60, exhausted_likely: false });
  const isMaps = !job?.source || job.source === 'google_maps';

  const loadCursor = useCallback(async () => {
    if (!job?.query?.trim()) return;
    try {
      const qs = new URLSearchParams({ query: job.query.trim(), location: (job.location || '').trim() });
      const res = await fetch(appendWebsiteScope(apiPath(`/gmaps/cursor?${qs.toString()}`), jobWebsiteId), { headers: authHeaders() });
      if (res.ok) setCursor(await res.json());
    } catch {
      // ignore
    }
  }, [job?.query, job?.location, jobWebsiteId]);

  useEffect(() => {
    if (job?.status === 'done' && isMaps) loadCursor();
  }, [job?.id, job?.status, loadCursor, isMaps]);

  if (!job || job.status !== 'done' || !isMaps) return null;

  const nextFrom = (cursor.delivered || 0) + 1;
  const nextTo = (cursor.delivered || 0) + batchSize;

  return (
    <div
      className={`rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
        cursor.exhausted_likely
          ? 'bg-amber-500/10 border-amber-500/30'
          : 'bg-violet-500/10 border-violet-500/30'
      }`}
      data-testid="lead-scraper-next-batch-panel"
    >
      <div className="text-sm text-white/80">
        {cursor.exhausted_likely ? (
          <p>Most businesses for this search are already collected (~{cursor.delivered} leads). Try a broader query or different location.</p>
        ) : (
          <>
            <p className="font-medium text-violet-100">Next page of unique leads</p>
            <p className="text-xs text-violet-200/80 mt-1">
              {cursor.delivered > 0
                ? <>You have <b>{cursor.delivered}</b> leads for this search. The next batch fetches unique leads <b>#{nextFrom}–#{nextTo}</b> (no duplicates from earlier runs).</>
                : <>Fetch the next <b>{batchSize}</b> unique businesses for this search.</>}
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => onFetchNext(job)}
        disabled={loading || cursor.exhausted_likely}
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5 shrink-0"
        data-testid="lead-scraper-fetch-next-batch-button"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        {loading ? 'Starting…' : `Fetch next ${batchSize}`}
      </button>
    </div>
  );
}

function JobCard({ job, selected, onView, onDelete }) {
  const sourceLabel = {
    google_maps: 'Local',
    linkedin_icp: 'LinkedIn ICP',
    hunter_discover: 'Companies',
    apollo_search: 'People',
    hunter_people: 'People',
  }[job.source] || job.source || 'Local';
  return (
    <div data-testid="lead-scraper-job-card" className={`bg-white/5 border rounded-xl p-4 cursor-pointer transition-all ${selected ? 'border-violet-500 bg-violet-500/10' : 'border-white/10 hover:border-white/20'}`} onClick={() => onView(job)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{job.query}</p>
          {job.location && <p className="text-xs text-white/50 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{job.location}</p>}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">{sourceLabel}</span>
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[job.status] || 'text-white/50 bg-white/10'}`}>
              {statusIcon[job.status]}{job.status}
            </span>
            {job.result_count > 0 && <span className="text-xs text-white/40">{job.result_count} leads</span>}
          </div>
          {job.status === 'running' && <ProgressBar value={job.progress || 0} />}
        </div>
        <button data-testid="lead-scraper-delete-job-button" onClick={e => { e.stopPropagation(); onDelete(job.id); }} className="text-white/20 hover:text-red-400 transition-colors flex-shrink-0">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-white/30 mt-2">{new Date(job.created_at).toLocaleString()}</p>
    </div>
  );
}

function ResultsTable({ jobId, job, resultCount, onSaveToCRM, selectedWebsiteId, onRetryEntity, onQualify, qualifying }) {
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('');
  const isLinkedIn = job?.source === 'linkedin_icp';
  const rowKey = (r, i) => r.place_id || r.external_id || r.linkedin_url || r.email || String(i);

  const load = useCallback(async (p, size = pageSize) => {
    setLoading(true);
    try {
      const scopedResultsUrl = appendWebsiteScope(apiPath(`/gmaps/jobs/${jobId}/results?page=${p}&page_size=${size}`), selectedWebsiteId);
      const res = await fetch(scopedResultsUrl, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Failed to load results'); return; }
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load results'); } finally { setLoading(false); }
  }, [jobId, selectedWebsiteId, pageSize]);

  useEffect(() => { load(page, pageSize); }, [page, pageSize, load, job?.qualified_count, job?.qualified_at]);

  const handlePageSizeChange = (nextSize) => {
    const size = Number(nextSize) || DEFAULT_RESULTS_PAGE_SIZE;
    setPageSize(size);
    setPage(1);
  };

  const toggleSelect = (key) => setSelected(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  const toggleAll = () => {
    const keys = results.map((r, i) => rowKey(r, i));
    selected.size === keys.length ? setSelected(new Set()) : setSelected(new Set(keys));
  };

  const handleExport = async (fmt) => {
    const res = await fetch(appendWebsiteScope(apiPath(`/gmaps/jobs/${jobId}/export/${fmt}`), selectedWebsiteId), { headers: authHeaders() });
    if (!res.ok) { toast.error('Export failed'); return; }
    const blob = await res.blob();
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `leads_${jobId.slice(0,8)}.${fmt}`; a.click();
    toast.success(`Exported as ${fmt.toUpperCase()}`);
  };

  const handleSaveToCRM = async () => {
    const placeIds = [...selected].filter((k) => results.some((r) => r.place_id && r.place_id === k));
    await onSaveToCRM(jobId, placeIds.length > 0 ? placeIds : []);
    setSelected(new Set());
  };

  const filtered = filter ? results.filter(r => [r.name,r.address,r.city,r.phone,r.website,r.email,r.contact_name,r.linkedin_url].some(v => v && v.toLowerCase().includes(filter.toLowerCase()))) : results;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3" data-testid="lead-scraper-results-toolbar">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input data-testid="lead-scraper-filter-input" className="w-full bg-white/10 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-violet-500" placeholder="Filter results..." value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-xs text-white/40">
          Per page
          <select
            data-testid="lead-scraper-page-size-select"
            className="bg-white/10 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-violet-500"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(e.target.value)}
          >
            {RESULTS_PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n} className="bg-gray-900">{n}</option>
            ))}
          </select>
        </label>
        <span className="text-xs text-white/40">{total} leads</span>
        {isLinkedIn && (
          <button
            type="button"
            data-testid="lead-scraper-qualify-button"
            onClick={() => onQualify?.(job)}
            disabled={!!qualifying}
            className="flex items-center gap-1.5 text-xs bg-emerald-600/80 hover:bg-emerald-500 disabled:opacity-50 text-white px-3 py-2 rounded-xl transition-colors"
          >
            {qualifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            {qualifying ? 'Qualifying…' : 'Qualify (ICP score)'}
          </button>
        )}
        <button data-testid="lead-scraper-export-csv-button" onClick={() => handleExport('csv')} className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-xl transition-colors"><Download className="w-3.5 h-3.5" /> CSV</button>
        <button data-testid="lead-scraper-export-json-button" onClick={() => handleExport('json')} className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-xl transition-colors"><Download className="w-3.5 h-3.5" /> JSON</button>
        <button data-testid="lead-scraper-save-crm-button" onClick={handleSaveToCRM} className="flex items-center gap-1.5 text-xs bg-violet-600 hover:bg-violet-500 text-white px-3 py-2 rounded-xl transition-colors"><Save className="w-3.5 h-3.5" />{selected.size > 0 ? `Save ${selected.size} to CRM` : 'Save All to CRM'}</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead data-testid="lead-scraper-results-header">
            <tr className="border-b border-white/10 bg-white/5">
              <th className="px-3 py-3 text-left"><button data-testid="lead-scraper-toggle-all-button" onClick={toggleAll}>{selected.size === results.length && results.length > 0 ? <CheckSquare className="w-4 h-4 text-violet-400" /> : <Square className="w-4 h-4 text-white/30" />}</button></th>
              <th className="px-3 py-3 text-left text-white/60 font-medium">{isLinkedIn ? 'Company / person' : 'Business'}</th>
              <th className="px-3 py-3 text-left text-white/60 font-medium">Contact</th>
              <th className="px-3 py-3 text-left text-white/60 font-medium">{isLinkedIn ? 'ICP' : 'Location'}</th>
              {!isLinkedIn && <th className="px-3 py-3 text-left text-white/60 font-medium">Rating</th>}
              <th className="px-3 py-3 text-left text-white/60 font-medium">Status</th>
              <th className="px-3 py-3 text-left text-white/60 font-medium">{isLinkedIn ? 'LinkedIn' : 'Maps'}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isLinkedIn ? 6 : 7} className="text-center py-12 text-white/40"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Loading results...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isLinkedIn ? 6 : 7} className="text-center py-12 text-white/40">No results found</td></tr>
            ) : filtered.map((r, i) => {
              const key = rowKey(r, i);
              return (
              <tr key={key} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid="lead-scraper-result-row">
                <td className="px-3 py-3"><button data-testid="lead-scraper-select-row-button" onClick={() => toggleSelect(key)}>{selected.has(key) ? <CheckSquare className="w-4 h-4 text-violet-400" /> : <Square className="w-4 h-4 text-white/20" />}</button></td>
                <td className="px-3 py-3">
                  <div className="flex items-start gap-2">
                    {r.photo_url && <img src={r.photo_url} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />}
                    <div><p className="text-white font-medium leading-tight">{r.name}</p>{r.primary_type && <span className="text-xs text-violet-400 capitalize">{r.primary_type.replace(/_/g,' ')}</span>}</div>
                  </div>
                </td>
                <td className="px-3 py-3 space-y-1" data-testid="lead-scraper-contact-cell">
                  {r.contact_name && <div className="text-xs text-white font-medium">{r.contact_name}</div>}
                  {r.contact_title && <div className="text-[11px] text-white/45">{r.contact_title}</div>}
                  {r.phone && <div className="flex items-center gap-1 text-xs text-white/70"><Phone className="w-3 h-3 text-green-400" /><a href={`tel:${r.phone}`} className="hover:text-white">{r.phone}</a></div>}
                  {r.email && <div className="flex items-center gap-1 text-xs text-white/70"><Mail className="w-3 h-3 text-blue-400" /><a href={`mailto:${r.email}`} className="hover:text-white">{r.email}</a></div>}
                  {r.website && <div className="flex items-center gap-1 text-xs text-white/70"><Globe className="w-3 h-3 text-purple-400" /><a href={r.website} target="_blank" rel="noreferrer" className="hover:text-white truncate max-w-32">{r.website.replace(/^https?:\/\//,'')}</a></div>}
                </td>
                <td className="px-3 py-3">
                  {isLinkedIn ? (
                    <div>
                      {r.icp_score != null ? (
                        <span className="text-sm text-emerald-300 font-medium" data-testid="lead-scraper-icp-score">{r.icp_score}/10</span>
                      ) : (
                        <span className="text-xs text-white/35">Not scored</span>
                      )}
                      {r.icp_reason && <p className="text-[11px] text-white/40 mt-1 max-w-40">{r.icp_reason}</p>}
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-white/70 max-w-40 leading-relaxed">{r.address}</p>
                      {r.city && <p className="text-xs text-white/40">{[r.city,r.country].filter(Boolean).join(', ')}</p>}
                    </>
                  )}
                </td>
                {!isLinkedIn && (
                  <td className="px-3 py-3">{r.rating > 0 && <div className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" /><span className="text-sm text-white">{r.rating}</span><span className="text-xs text-white/40">({r.review_count?.toLocaleString()})</span></div>}</td>
                )}
                <td className="px-3 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.business_status==='OPERATIONAL'?'bg-green-500/20 text-green-400':r.business_status==='CLOSED_TEMPORARILY'?'bg-yellow-500/20 text-yellow-400':r.business_status==='CLOSED_PERMANENTLY'?'bg-red-500/20 text-red-400':'bg-white/10 text-white/40'}`}>{r.business_status?r.business_status.replace(/_/g,' '):'Unknown'}</span>
                  {r.crm_sync_status && <p className="text-[11px] mt-1 text-white/40" data-testid="lead-scraper-crm-sync-status">CRM: {r.crm_sync_status.replace(/_/g, ' ')}</p>}
                  {(r.enrichment_status === 'partial' || r.enrichment_status === 'failed') && r.entity_id && (
                    <button
                      type="button"
                      onClick={() => onRetryEntity(r.entity_id)}
                      className="mt-2 inline-flex items-center gap-1 rounded-lg bg-amber-500/15 px-2 py-1 text-[11px] text-amber-200 hover:bg-amber-500/25"
                      data-testid="lead-scraper-retry-entity-button"
                    >
                      <RotateCcw className="w-3 h-3" /> Retry enrichment
                    </button>
                  )}
                </td>
                <td className="px-3 py-3">
                  {isLinkedIn
                    ? (r.linkedin_url && <a href={r.linkedin_url} target="_blank" rel="noreferrer" className="text-white/30 hover:text-violet-400 transition-colors" title="View LinkedIn profile"><ExternalLink className="w-4 h-4" /></a>)
                    : (r.google_maps_url && <a href={r.google_maps_url} target="_blank" rel="noreferrer" className="text-white/30 hover:text-violet-400 transition-colors" title="View on Google Maps"><ExternalLink className="w-4 h-4" /></a>)}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      {total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-white/40">Showing {(page-1)*pageSize+1}–{Math.min(page*pageSize,total)} of {total}</p>
          <div className="flex items-center gap-2">
            <button data-testid="lead-scraper-page-prev-button" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 transition-colors"><ChevronLeft className="w-4 h-4 text-white" /></button>
            <span className="text-xs text-white/60">{page} / {totalPages}</span>
            <button data-testid="lead-scraper-page-next-button" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 transition-colors"><ChevronRight className="w-4 h-4 text-white" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeadScraper() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [websites, setWebsites] = useState([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState('');
  const [websitesLoading, setWebsitesLoading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewChannels, setReviewChannels] = useState(['email', 'sms']);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditCosts, setCreditCosts] = useState({ scrape_lead: 1, email_enrichment: 1, contact_enrichment: 1 });
  const [creditPacks, setCreditPacks] = useState(DEFAULT_LEAD_CREDIT_PACKS);
  const [creditsEnabled, setCreditsEnabled] = useState(true);
  const [stripeConfigured, setStripeConfigured] = useState(null);
  const [nextBatchLoading, setNextBatchLoading] = useState(false);
  const [qualifying, setQualifying] = useState(false);
  const pollRef = useRef(null);

  const loadCredits = useCallback(async () => {
    try {
      const [balRes, catRes] = await Promise.all([
        fetch(apiPath('/lead-credits/balance'), { headers: authHeaders() }),
        fetch(apiPath('/lead-credits/catalog'), { headers: authHeaders() }),
      ]);
      const balData = balRes.ok ? await balRes.json().catch(() => ({})) : {};
      const catData = catRes.ok ? await catRes.json().catch(() => ({})) : {};
      setCreditsEnabled(balData.enabled !== false && catData.enabled !== false);
      setCreditBalance(balData.balance ?? 0);
      if (balData.costs || catData.costs) setCreditCosts(balData.costs || catData.costs);
      setCreditPacks((catData.packs && catData.packs.length) ? catData.packs : DEFAULT_LEAD_CREDIT_PACKS);
      if (typeof catData.stripe_configured === 'boolean') setStripeConfigured(catData.stripe_configured);
    } catch {
      setCreditPacks(DEFAULT_LEAD_CREDIT_PACKS);
    }
  }, []);

  const completeCreditsReturn = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await fetch(apiPath(`/lead-credits/checkout/${encodeURIComponent(sessionId)}/status`), {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (data.payment_status === 'paid' || data.success) {
        toast.success(data.message || `Credits added. Balance: ${data.balance}`);
        setCreditBalance(data.balance ?? creditBalance);
        await loadCredits();
      }
    } catch {
      toast.error('Could not verify credit purchase');
    } finally {
      setSearchParams({});
    }
  }, [creditBalance, loadCredits, setSearchParams]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  useEffect(() => {
    const payment = searchParams.get('credits_payment');
    const sessionId = searchParams.get('session_id');
    if (payment === 'cancelled') {
      toast.message('Credit purchase cancelled');
      setSearchParams({});
      return;
    }
    if (sessionId && payment) completeCreditsReturn(sessionId);
  }, [searchParams, completeCreditsReturn, setSearchParams]);

  const normalizeWebsites = (items = []) => (
    items
      .filter(Boolean)
      .map((website) => ({
        ...website,
        name: website.name || website.website_name || website.slug || website.custom_domain || website.domain || 'Untitled website',
      }))
  );

  const loadWebsites = useCallback(async () => {
    setWebsitesLoading(true);
    try {
      const res = await fetch(apiPath('/websites'), { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        toast.error(data?.detail || 'Failed to load websites');
        return;
      }
      const normalized = normalizeWebsites(Array.isArray(data) ? data : []);
      setWebsites(normalized);
      if (selectedWebsiteId && !normalized.some((website) => website.id === selectedWebsiteId)) {
        setSelectedWebsiteId('');
      }
    } catch {
      toast.error('Failed to load websites');
    } finally {
      setWebsitesLoading(false);
    }
  }, [selectedWebsiteId]);

  const loadJobs = useCallback(async () => {
    try {
      const scopedJobsUrl = appendWebsiteScope(apiPath('/gmaps/jobs'), selectedWebsiteId);
      const res = await fetch(scopedJobsUrl, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const next = data.jobs || [];
      setJobs(next);
      // Refresh the currently-selected job in-place, but ONLY if a meaningful
      // field actually changed — otherwise we'd recreate the object every poll
      // and trigger an infinite reload of <ResultsTable />.
      setSelectedJob(prev => {
        if (!prev) return prev;
        const updated = next.find(j => j.id === prev.id);
        if (!updated) return null;
        const changed = ['status', 'progress', 'result_count', 'error']
          .some(k => updated[k] !== prev[k]);
        return changed ? { ...prev, ...updated } : prev;
      });
    } catch (e) {
      // ignore intermittent polling errors
    } finally { setLoadingJobs(false); }
  }, [selectedWebsiteId]);  // <- scoped by workspace selection

  useEffect(() => {
    loadWebsites();
  }, [loadWebsites]);

  useEffect(() => {
    setSelectedJob(null);
    loadJobs();
    pollRef.current = setInterval(() => {
      // We always re-fetch on a steady cadence; loadJobs short-circuits cheaply
      // if nothing changed. This avoids the prior "interval recreated every
      // setSelectedJob call" doom loop.
      loadJobs();
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [loadJobs, selectedWebsiteId]);

  const handleJobCreated = (job) => {
    setJobs(prev => [job, ...prev]);
    setSelectedJob(job);
    loadCredits();
  };

  const handleFetchNextBatch = async (job) => {
    if (!job?.query?.trim() || nextBatchLoading) return;
    setNextBatchLoading(true);
    try {
      const payload = buildScrapePayloadFromJob(job, selectedWebsiteId);
      const res = await fetch(apiPath('/gmaps/jobs'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        if (res.status === 402) {
          const detail = data.detail || {};
          const msg = typeof detail === 'string' ? detail : detail.message || 'Insufficient lead credits';
          toast.error(`${msg} — buy more credits to continue.`, { duration: 8000 });
          loadCredits();
          return;
        }
        if (res.status === 503) {
          toast.error('Lead search is temporarily unavailable. Please try again later or contact support.', { duration: 8000 });
        } else {
          toast.error(data.detail || `Error ${res.status}: Failed to start next batch`);
        }
        return;
      }
      toast.success(`Fetching next ${payload.max_results} unique leads…`);
      handleJobCreated(data);
    } catch (err) {
      toast.error(err.message || 'Failed to start next batch');
    } finally {
      setNextBatchLoading(false);
    }
  };

  const handleQualify = async (job) => {
    if (!job?.id || qualifying) return;
    setQualifying(true);
    try {
      const res = await fetch(apiPath('/lead-jobs/qualify'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          job_id: job.id,
          icp_text: job.icp_text || job.query || '',
          website_id: jobWebsiteScope(job),
          max_leads: Math.min(Number(job.result_count || 25), 25),
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : 'Qualification failed — check TAVILY_API_KEY / OPENAI_API_KEY in Settings.';
        toast.error(detail, { duration: 9000 });
        return;
      }
      toast.success(`Qualified ${data.qualified_count || 0} leads`);
      setSelectedJob((prev) => prev && prev.id === job.id ? { ...prev, qualified_count: data.qualified_count, qualified_at: new Date().toISOString() } : prev);
      await loadJobs();
    } catch (err) {
      toast.error(err.message || 'Qualification failed');
    } finally {
      setQualifying(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job and all its results?')) return;
    try {
      await fetch(appendWebsiteScope(apiPath(`/gmaps/jobs/${jobId}`), selectedWebsiteId), { method:'DELETE', headers: authHeaders() });
      setJobs(prev => prev.filter(j => j.id!==jobId));
      if (selectedJob?.id===jobId) setSelectedJob(null);
      toast.success('Job deleted');
    } catch (e) { toast.error('Failed to delete job'); }
  };

  const handleSaveToCRM = async (jobId, placeIds) => {
    const scopeId = jobWebsiteScope(selectedJob);
    try {
      const res = await fetch(apiPath(`/gmaps/jobs/${jobId}/save-to-crm`), {
        method:'POST', headers: authHeaders(), body: JSON.stringify({ place_ids: placeIds, website_id: scopeId }),
      });
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!res.ok) { toast.error(data.detail||'Failed to save'); return; }
      const skippedCount = Number(data.skipped_count || 0);
      if (skippedCount > 0) {
        toast.success(`${data.saved_count} leads saved. ${skippedCount} already existed and were skipped.`);
      } else {
        toast.success(`${data.saved_count} leads saved to CRM`);
      }
    } catch (e) { toast.error('Failed to save to CRM'); }
  };

  const handleMarketingSync = async (channels, entityIds = []) => {
    if (!selectedJob?.id) return;
    const scopeId = jobWebsiteScope(selectedJob);
    try {
      const res = await fetch(apiPath(`/leads/jobs/${selectedJob.id}/sync-marketing`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ channels, website_id: scopeId, entity_ids: entityIds }),
      });
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!res.ok) {
        toast.error(formatApiError(data, 'Failed to sync audiences'));
        return;
      }
      const counts = data.counts || {};
      toast.success(`Synced Email ${counts.email || 0} • SMS ${counts.sms || 0} • WhatsApp ${counts.whatsapp || 0}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to sync audiences'));
    }
  };

  const handleOpenReview = (channels) => {
    setReviewChannels(channels);
    setReviewOpen(true);
  };

  const handleRetryEntity = async (entityId) => {
    if (!selectedJob?.id || !entityId) return;
    const scopeId = jobWebsiteScope(selectedJob);
    try {
      const res = await fetch(apiPath(`/leads/jobs/${selectedJob.id}/retry-enrichment`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ website_id: scopeId, entity_ids: [entityId] }),
      });
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!res.ok) {
        toast.error(data.detail || 'Failed to queue retry');
        return;
      }
      toast.success(`Queued ${data.queued || 0} retry${data.skipped ? ` · skipped ${data.skipped}` : ''}`);
    } catch {
      toast.error('Failed to queue retry');
    }
  };

  const handleBulkRetry = async () => {
    if (!selectedJob?.id) return;
    const scopeId = jobWebsiteScope(selectedJob);
    try {
      const reviewRes = await fetch(apiPath(`/leads/jobs/${selectedJob.id}/review-marketing`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ website_id: scopeId }),
      });
      const reviewText = await reviewRes.text();
      let reviewData = {};
      try { reviewData = reviewText ? JSON.parse(reviewText) : {}; } catch { reviewData = {}; }
      if (!reviewRes.ok) {
        toast.error(reviewData.detail || 'Failed to load retry candidates');
        return;
      }
      const partialIds = (reviewData.entities || []).filter((entity) => entity.enrichment_status === 'partial' || entity.enrichment_status === 'failed').map((entity) => entity.id);
      if (!partialIds.length) {
        toast.success('No partial leads need retry right now');
        return;
      }
      const res = await fetch(apiPath(`/leads/jobs/${selectedJob.id}/retry-enrichment`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ website_id: scopeId, entity_ids: partialIds }),
      });
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!res.ok) {
        toast.error(data.detail || 'Failed to queue retries');
        return;
      }
      toast.success(`Queued ${data.queued || 0} retries${data.skipped ? ` · skipped ${data.skipped}` : ''}`);
    } catch {
      toast.error('Failed to queue retries');
    }
  };

  const runningJobs = jobs.filter(j => j.status==='pending'||j.status==='running');
  const doneJobs = jobs.filter(j => j.status==='done');

  const currentWebsite = websites.find((website) => website.id === selectedWebsiteId) || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
      <div className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button data-testid="lead-scraper-back-button" onClick={() => navigate('/dashboard')} className="text-white/40 hover:text-white transition-colors"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2"><MapPin className="w-5 h-5 text-violet-400" />Lead Scraper</h1>
              <p className="text-xs text-white/40">Local business intelligence — 33+ data fields per lead</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <LeadCreditsPanel
              balance={creditBalance}
              costs={creditCosts}
              packs={creditPacks}
              enabled={creditsEnabled}
              stripeConfigured={stripeConfigured}
              onRefresh={loadCredits}
              onBalanceChange={setCreditBalance}
            />
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-1">
              <span className="text-[11px] text-white/60">Workspace</span>
              <select
                value={selectedWebsiteId}
                onChange={(e) => setSelectedWebsiteId(e.target.value)}
                className="h-7 rounded-md bg-black/40 border border-white/10 px-2 text-xs focus:outline-none focus:border-violet-500"
                data-testid="lead-scraper-workspace-select"
              >
                <option value="">Account-wide</option>
                {websites.map((website) => (
                  <option key={website.id} value={website.id}>{website.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={loadWebsites}
                disabled={websitesLoading}
                className="p-1 text-white/60 hover:text-white disabled:opacity-40"
                data-testid="lead-scraper-refresh-websites-button"
                title="Refresh websites"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${websitesLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="hidden md:flex items-center gap-4 text-xs text-white/40">
              <span className="flex items-center gap-1"><BarChart2 className="w-3.5 h-3.5 text-violet-400" />{doneJobs.reduce((s,j)=>s+(j.result_count||0),0).toLocaleString()} total leads</span>
              {runningJobs.length>0&&<span className="flex items-center gap-1 text-blue-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />{runningJobs.length} running</span>}
            </div>
            <button data-testid="lead-scraper-refresh-jobs-button" onClick={loadJobs} className="p-2 text-white/40 hover:text-white transition-colors" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 py-8">
        <ModuleDemoVideo moduleKey="lead-scraper" className="mb-5" />
        <div className="mb-5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-white/70" data-testid="lead-scraper-scope-banner">
          {selectedWebsiteId
            ? <>Website workspace: <span className="text-white font-semibold">{currentWebsite?.name || 'Selected website'}</span>. New scrape jobs and CRM saves stay scoped to this website.</>
            : <>Account-wide workspace active. New scrape jobs and CRM saves are shared at account level.</>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="space-y-6">
            <NewJobForm
              onCreated={handleJobCreated}
              selectedWebsiteId={selectedWebsiteId}
              onInsufficientCredits={loadCredits}
              creditBalance={creditBalance}
            />
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white/70">Recent Jobs</h3>
                <span className="text-xs text-white/30">{jobs.length} total</span>
              </div>
              {loadingJobs ? (
                <div className="text-center py-8 text-white/30"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading jobs...</div>
              ) : jobs.length===0 ? (
                <div className="text-center py-8 text-white/30 bg-white/5 rounded-xl border border-white/10"><Search className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No jobs yet</p><p className="text-xs mt-1">Create your first scrape job above</p></div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {jobs.map(job => <JobCard key={job.id} job={job} selected={selectedJob?.id===job.id} onView={setSelectedJob} onDelete={handleDeleteJob} />)}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            {!selectedJob ? (
              <div className="flex items-center justify-center h-96 bg-white/5 border border-white/10 rounded-2xl">
                <div className="text-center text-white/30">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-medium">Select a job to view results</p>
                  <p className="text-sm mt-1">Or create a new scrape job to get started</p>
                </div>
              </div>
            ) : (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{selectedJob.query}</h3>
                    {selectedJob.location&&<p className="text-sm text-white/50 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" />{selectedJob.location}</p>}
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[selectedJob.status]||'text-white/50 bg-white/10'}`}>{statusIcon[selectedJob.status]}{selectedJob.status}</span>
                      <span className="text-xs text-white/40">{selectedJob.result_count||0} leads found</span>
                      <span className="text-xs text-white/30">{new Date(selectedJob.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <button data-testid="lead-scraper-close-selected-job-button" onClick={() => setSelectedJob(null)} className="text-white/30 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {[`Max: ${selectedJob.max_results}`, selectedJob.min_rating>0?`Rating: ${selectedJob.min_rating}+`:null, selectedJob.require_phone?'Has Phone':null, selectedJob.require_website?'Has Website':null, selectedJob.extract_emails?'Emails Extracted':null].filter(Boolean).map(tag => <span key={tag} className="text-xs bg-white/10 text-white/60 px-2 py-0.5 rounded-full">{tag}</span>)}
                </div>

                {selectedJob.status==='running'&&(
                  <div>
                    <div className="flex items-center justify-between text-xs text-white/50 mb-1"><span>Scraping in progress...</span><span>{selectedJob.progress||0}%</span></div>
                    <ProgressBar value={selectedJob.progress||0} />
                    <p className="text-xs text-white/30 mt-2">Auto-refreshing every 3s. Phase 1: collecting places. Phase 2: enriching with 33+ data fields.</p>
                  </div>
                )}

                {selectedJob.status==='pending'&&<div className="text-center py-8 text-white/40"><Clock className="w-8 h-8 mx-auto mb-2 animate-pulse" /><p>Job queued, starting soon...</p></div>}

                {selectedJob.status==='failed'&&(
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-400">Job Failed</p>
                        <p className="text-xs text-red-300/70 mt-1">{selectedJob.error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <FetchNextBatchPanel
                  job={selectedJob}
                  loading={nextBatchLoading}
                  onFetchNext={handleFetchNextBatch}
                />

                <PipelineOverview
                  jobId={selectedJob.id}
                  jobWebsiteId={selectedJob.website_id || ''}
                  jobStatus={selectedJob.status}
                  jobProgress={selectedJob.progress || 0}
                  jobResultCount={selectedJob.result_count || 0}
                  onOpenReview={handleOpenReview}
                  onBulkRetry={handleBulkRetry}
                />

                {selectedJob.status==='done'&&selectedJob.result_count>0&&<ResultsTable jobId={selectedJob.id} job={selectedJob} resultCount={selectedJob.result_count} onSaveToCRM={handleSaveToCRM} selectedWebsiteId={jobWebsiteScope(selectedJob)} onRetryEntity={handleRetryEntity} onQualify={handleQualify} qualifying={qualifying} />}
                {selectedJob.status==='done'&&selectedJob.result_count===0&&<div className="text-center py-8 text-white/40"><Search className="w-8 h-8 mx-auto mb-2 opacity-30" /><p>No results found for this query.</p><p className="text-xs mt-1">Try a broader search or different location.</p></div>}
              </div>
            )}
          </div>
        </div>
      </div>
      <MarketingReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        jobId={selectedJob?.id}
        selectedWebsiteId={jobWebsiteScope(selectedJob)}
        defaultChannels={reviewChannels}
        onConfirmSync={async (channels, entityIds) => {
          await handleMarketingSync(channels, entityIds);
          setReviewOpen(false);
        }}
      />
    </div>
  );
}
